import { OrderStatus } from '@features/siteStatusV2/constants';
import { Order } from '@features/siteStatusV2/types';
import theme from '@theme';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface StatusBadgeProps {
  order: Order;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const badgeName = useMemo(() => {
    switch (order.status) {
      case OrderStatus.ACTIVE:
        return t('orderDetails.badges.status.active');
      case OrderStatus.TERMINATED:
        return t('orderDetails.badges.status.terminated');
      case OrderStatus.IN_PROGRESS:
        return t('orderDetails.badges.status.inProgress');
      case OrderStatus.DELETED:
        return t('orderDetails.badges.status.deleted');
      case OrderStatus.FINISHED:
        return t('orderDetails.badges.status.finished');
      case OrderStatus.CANCELLED:
        return t('orderDetails.badges.status.canceled');
      default:
        return order.rawStatus ?? 'N/A';
    }
  }, [order.status]);
  return <StatusBadgeContainer status={order.status}>{badgeName}</StatusBadgeContainer>;
};

interface BadgeContainerProps {
  status: OrderStatus;
}

const getColor = (status: OrderStatus): { border: string; text: string } => {
  let border: string;
  let text: string;
  switch (status) {
    case OrderStatus.ACTIVE:
      border = theme.palette.green[200];
      text = theme.palette.green[600];
      break;
    case OrderStatus.TERMINATED:
      border = theme.palette.red[200];
      text = theme.palette.red[600];
      break;
    case OrderStatus.IN_PROGRESS:
      border = theme.palette.blue[200];
      text = theme.palette.blue[600];
      break;
    case OrderStatus.DELETED:
      border = theme.palette.red[200];
      text = theme.palette.red[600];
      break;
    case OrderStatus.FINISHED:
      border = theme.palette.orange[200];
      text = theme.palette.orange[600];
      break;
    case OrderStatus.CANCELLED:
      border = theme.palette.red[200];
      text = theme.palette.red[600];
      break;
    default:
      border = theme.palette.grey[200];
      text = theme.palette.grey[600];
      break;
  }
  return {
    border,
    text
  };
};

const StatusBadgeContainer = styled.span<BadgeContainerProps>`
  width: fit-content;
  height: fit-content;
  padding-top: 0.125rem;
  padding-left: 0.5rem;
  padding-bottom: 0.125rem;
  padding-right: 0.5rem;
  border-width: 1px;
  border-radius: 0.25rem;
  border-color: ${({ status }) => getColor(status).border};
  border-style: solid;
  font-size: 0.625rem;
  line-height: 0.75rem;
  font-weight: 500;
  text-align: center;
  color: ${({ status }) => getColor(status).text};
`;

export default StatusBadge;
